/**
 * subtitle-overlay.js — render SubtitleParser cues over a playing <video>.
 *
 * Why not native <track>: ::cue styling is inconsistent across engines, the
 * cue layer disappears under wrap-fullscreen on some browsers, we need a live
 * timing offset (subtitles from a different release of the same movie), and
 * native tracks cannot show two languages at once. The overlay div lives
 * INSIDE the player wrap, so it survives wrap-FS (TimbricaPlayer fsPreferWrap)
 * and CSS pseudo-FS.
 *
 *   var ov = SubtitleOverlay.create(playerWrapEl, videoEl);
 *   ov.loadCues(SubtitleParser.parse(text, {preserveMarkup:true}));
 *   ov.setVisible(true);
 *   ov.setSize(22);          // px at default player width, scales in FS
 *   ov.setOffset(+1.5);      // s — positive shows subtitles later
 *   ov.loadSecondary(cues2); // second language under the first (dual subs)
 *   ov.setStyle({ color:'#fff', outline:'outline', bg:0.72, position:9 });
 *   ov.destroy();
 *
 * Cues carrying a sanitized `html` field (preserveMarkup) render with their
 * line breaks and <i>/<b>/<u>; plain cues fall back to textContent. Anything
 * without `html` is NEVER passed to innerHTML.
 *
 * DOM is written only when the set of active cues actually changes; the rAF
 * loop runs only while the video plays.
 */
(function () {
    'use strict';

    var CSS_ID = 'subov-css';
    var CSS = [
        /* --subov-pos is the distance from the bottom; the controls bar lifts
           the layer by a fixed amount so subtitles never sit under it. */
        '.subov-layer{position:absolute;left:0;right:0;bottom:var(--subov-pos,9%);z-index:15;',
        'display:flex;flex-direction:column;align-items:center;gap:.25em;',
        'justify-content:flex-end;pointer-events:none;padding:0 4%;transition:bottom .15s}',
        '.subov-cue{max-width:88%;color:var(--subov-color,#fff);font-size:var(--subov-size,22px);',
        'font-family:var(--subov-font,inherit);line-height:1.35;font-weight:var(--subov-weight,600);',
        'text-align:center;border-radius:8px;padding:.25em .6em;white-space:pre-line;',
        'background:var(--subov-bg,rgba(8,10,14,.72));text-shadow:var(--subov-shadow,0 1px 2px rgba(0,0,0,.8))}',
        '.subov-cue.secondary{font-size:calc(var(--subov-size,22px) * .82);opacity:.92;',
        'color:var(--subov-color2,#cfe0ff)}',
        '.subov-cue i{font-style:italic}.subov-cue b{font-weight:800}.subov-cue u{text-decoration:underline}',
        '.tp-wrap.tp-show-controls .subov-layer{bottom:calc(var(--subov-pos,9%) + 30px)}',
        '.tp-wrap.paused .subov-layer{bottom:calc(var(--subov-pos,9%) + 30px)}',
        /* The primary track is drawn by someone else (AssRenderer — the file's
           own styles own the bottom of the frame): only the second language is
           ours, and it goes to the TOP, as mpv puts a secondary track. */
        '.subov-layer.subov-primary-off .subov-cue:not(.secondary){display:none}',
        '.subov-layer.subov-primary-off,.tp-wrap.tp-show-controls .subov-layer.subov-primary-off,.tp-wrap.paused .subov-layer.subov-primary-off{top:5%;bottom:auto;justify-content:flex-start}'
    ].join('');

    /* 8-way text outline — reads on any background. -webkit-text-stroke is
       not used: it thins the glyph interior and mangles Cyrillic/Arabic. */
    var OUTLINE_SHADOW = (function () {
        var o = [], r = 1.2;
        for (var a = 0; a < 8; a++) {
            var rad = a * Math.PI / 4;
            o.push((Math.cos(rad) * r).toFixed(2) + 'px ' + (Math.sin(rad) * r).toFixed(2) + 'px 0 #000');
        }
        return o.join(',');
    })();

    var SHADOWS = {
        none: 'none',
        shadow: '0 1px 2px rgba(0,0,0,.8)',
        outline: OUTLINE_SHADOW
    };

    function create(container, video) {
        var layer = document.createElement('div');
        layer.className = 'subov-layer';
        layer.setAttribute('aria-live', 'off');
        if (!document.getElementById(CSS_ID)) {
            var s = document.createElement('style');
            s.id = CSS_ID;
            s.textContent = CSS;
            document.head.appendChild(s);
        }
        container.appendChild(layer);

        var tracks = {
            primary:   { cues: [], offset: 0, maxDur: 0 },
            secondary: { cues: [], offset: 0, maxDur: 0 }
        };
        var visible = false;
        var lastKey = null;
        var raf = null;
        var style = { size: 22, color: '#fff', color2: '#cfe0ff',
                      outline: 'shadow', bg: 0.72, position: 9, font: '', weight: 600 };

        function prep(list) {
            var cues = (list || []).slice().sort(function (a, b) { return a.start - b.start; });
            var maxDur = 0;
            for (var i = 0; i < cues.length; i++) {
                var d = cues[i].end - cues[i].start;
                if (d > maxDur) maxDur = d;
            }
            return { cues: cues, maxDur: maxDur || 0 };
        }

        /* All cues active at t. Overlapping cues are normal in ASS (a sign and
           a line of dialogue at once) — showing only one drops the other. */
        function activeAt(track, t) {
            var cues = track.cues;
            if (!cues.length) return [];
            // binary search: last index whose start <= t
            var lo = 0, hi = cues.length - 1, hit = -1;
            while (lo <= hi) {
                var mid = (lo + hi) >> 1;
                if (cues[mid].start <= t) { hit = mid; lo = mid + 1; }
                else hi = mid - 1;
            }
            if (hit < 0) return [];
            var out = [];
            // scan back only as far as the longest cue could still reach
            for (var i = hit; i >= 0; i--) {
                if (t - cues[i].start > track.maxDur) break;
                if (cues[i].end >= t) out.push(cues[i]);
            }
            return out.reverse();
        }

        function paint(el, cue) {
            if (cue.html) el.innerHTML = cue.html;   // sanitized by SubtitleParser
            else el.textContent = cue.text || '';
        }

        function render() {
            var t = video.currentTime;
            var p = visible ? activeAt(tracks.primary, t - tracks.primary.offset) : [];
            var s = visible ? activeAt(tracks.secondary, t - tracks.secondary.offset) : [];

            var key = p.map(function (c) { return 'p' + c.index; }).join('|') + '#' +
                      s.map(function (c) { return 's' + c.index; }).join('|');
            if (key === lastKey) return;
            lastKey = key;

            layer.innerHTML = '';
            p.forEach(function (c) {
                var d = document.createElement('div');
                d.className = 'subov-cue';
                d.setAttribute('dir', 'auto');
                paint(d, c);
                layer.appendChild(d);
            });
            s.forEach(function (c) {
                var d = document.createElement('div');
                d.className = 'subov-cue secondary';
                d.setAttribute('dir', 'auto');
                paint(d, c);
                layer.appendChild(d);
            });
        }

        function loop() {
            render();
            raf = video.paused ? null : requestAnimationFrame(loop);
        }
        function start() {
            if (!raf && visible && (tracks.primary.cues.length || tracks.secondary.cues.length)) loop();
        }
        function repaint() { lastKey = null; render(); start(); }

        function onPlay() { start(); }
        function onSeek() { repaint(); }
        video.addEventListener('play', onPlay);
        video.addEventListener('seeked', onSeek);
        video.addEventListener('pause', onSeek); // repaint the final frame's cue

        function applyStyle() {
            layer.style.setProperty('--subov-size', Math.max(12, +style.size || 22) + 'px');
            layer.style.setProperty('--subov-color', style.color || '#fff');
            layer.style.setProperty('--subov-color2', style.color2 || '#cfe0ff');
            layer.style.setProperty('--subov-shadow', SHADOWS[style.outline] || SHADOWS.shadow);
            var a = Math.max(0, Math.min(1, +style.bg));
            layer.style.setProperty('--subov-bg', a > 0 ? 'rgba(8,10,14,' + a.toFixed(2) + ')' : 'transparent');
            layer.style.setProperty('--subov-pos', Math.max(0, Math.min(60, +style.position || 0)) + '%');
            layer.style.setProperty('--subov-font', style.font || 'inherit');
            layer.style.setProperty('--subov-weight', String(style.weight || 600));
        }
        applyStyle();

        return {
            loadCues: function (list) {
                var r = prep(list);
                tracks.primary.cues = r.cues; tracks.primary.maxDur = r.maxDur;
                repaint();
            },
            loadSecondary: function (list) {
                var r = prep(list);
                tracks.secondary.cues = r.cues; tracks.secondary.maxDur = r.maxDur;
                repaint();
            },
            hasSecondary: function () { return tracks.secondary.cues.length > 0; },
            setVisible: function (on) { visible = !!on; repaint(); },
            /* Keep the primary cues (currentCue() and sync still read them) but
               do not draw them — another renderer is drawing that track. */
            setPrimaryHidden: function (on) { layer.classList.toggle('subov-primary-off', !!on); },
            isVisible: function () { return visible; },
            setSize: function (px) { style.size = Math.max(12, +px || 22); applyStyle(); },
            setOffset: function (sec) {
                tracks.primary.offset = +sec || 0; repaint();
            },
            setSecondaryOffset: function (sec) {
                tracks.secondary.offset = +sec || 0; repaint();
            },
            setStyle: function (patch) {
                Object.keys(patch || {}).forEach(function (k) {
                    if (k in style) style[k] = patch[k];
                });
                applyStyle();
            },
            getStyle: function () {
                var copy = {}; Object.keys(style).forEach(function (k) { copy[k] = style[k]; });
                return copy;
            },
            /* Currently-shown primary text — used by "copy this line" and by
               the speech-anchor sync flow. */
            currentText: function () {
                var a = activeAt(tracks.primary, video.currentTime - tracks.primary.offset);
                return a.length ? a[a.length - 1].text : '';
            },
            currentCue: function () {
                var a = activeAt(tracks.primary, video.currentTime - tracks.primary.offset);
                return a.length ? a[a.length - 1] : null;
            },
            destroy: function () {
                if (raf) cancelAnimationFrame(raf);
                video.removeEventListener('play', onPlay);
                video.removeEventListener('seeked', onSeek);
                video.removeEventListener('pause', onSeek);
                layer.remove();
            }
        };
    }

    window.SubtitleOverlay = { create: create, OUTLINE_SHADOW: OUTLINE_SHADOW };
})();
